import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Play, Trophy, Globe, Users } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';

export default function About() {
  const navigate = useNavigate();

  const pillars = [
    { icon: Play, title: 'Live Matches', desc: 'Watch every kick, tackle and goal as it happens across the continent.' },
    { icon: Trophy, title: 'Exclusive Competitions', desc: 'AFCON, CAF Champions League, local leagues and more — all in one place.' },
    { icon: Globe, title: 'Original Programming', desc: 'Documentaries, analysis and stories that bring African sport to life.' },
    { icon: Users, title: 'For the Community', desc: 'Built by African sport fans, for African sport fans worldwide.' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      {/* Opening Section — Brand Statement */}
      <section className="relative pt-32 pb-24 px-6 text-center overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1600&q=80')] bg-cover bg-center opacity-[0.06]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/60 to-[#0a0a0a]" />

        <motion.div
          className="relative z-10 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1 mb-8">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-emerald-400 text-sm font-medium tracking-widest uppercase">About AfroSports</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            The Home of<br />African Sport.
          </h1>

          <p className="text-lg sm:text-xl text-gray-400 leading-relaxed">
            Live matches. Exclusive competitions. Original programming — all in one place.
          </p>
        </motion.div>
      </section>

      {/* Mission */}
      <section className="max-w-3xl mx-auto px-6 pb-16 text-center">
        <motion.p
          className="text-gray-300 text-lg leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          AfroSports Stream was built to solve a simple problem: African sport deserves a dedicated home.
          Too many great matches are scattered, geo-blocked, or just never broadcast. We're changing that —
          bringing the full richness of football, athletics and beyond to fans everywhere.
        </motion.p>
      </section>

      {/* Four Pillars */}
      <section className="max-w-5xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              className="rounded-xl border border-white/5 bg-white/[0.03] p-6 flex gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <p.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1">{p.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/5 py-20 text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-white mb-4">Ready to watch?</h2>
          <p className="text-gray-400 mb-8">Join thousands of fans streaming African sport live.</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Button
              onClick={() => navigate('/browse')}
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-3 rounded-full font-semibold"
              data-testid="about-browse-btn"
            >
              Browse Content
            </Button>
            <Button
              onClick={() => navigate('/signup')}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 px-8 py-3 rounded-full"
              data-testid="about-signup-btn"
            >
              Create Account
            </Button>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
